/**
 * Helpers puros de validación usados por WaChatComposer (1275 LOC).
 *
 * Extraído del componente principal para test sin DOM/File mocks complejos.
 * NO depender de React aquí — son funciones puras.
 */

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

export const ALLOWED_VIDEO_TYPES = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/3gpp',
] as const;

export const ALLOWED_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/ogg',
  'audio/webm',
  'audio/aac',
  'audio/wav',
  'audio/mp4',
] as const;

export const ALLOWED_DOC_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip', 'rar',
] as const;

/**
 * Shape mínima que necesita validateMediaFile. NO importa File real (más fácil
 * para tests — no se necesita Blob/File globals).
 */
export interface MediaFileLike {
  size: number;
  type: string;
  name: string;
}

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Valida que un archivo cumpla los límites de tamaño + tipo permitidos para
 * envío por WhatsApp.
 *
 * Reglas:
 *   - size > 50MB → invalid
 *   - mime image/ video/ audio/ permitido → valid
 *   - mime no permitido pero ext en allowlist docs → valid
 *   - otherwise → invalid
 */
export function validateMediaFile(file: MediaFileLike): ValidationResult {
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `Archivo demasiado grande (${sizeMB}MB). Máximo permitido: 50MB.`,
    };
  }
  const mime = file.type || '';
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const isImage = (ALLOWED_IMAGE_TYPES as readonly string[]).includes(mime);
  const isVideo = (ALLOWED_VIDEO_TYPES as readonly string[]).includes(mime);
  const isAudio = (ALLOWED_AUDIO_TYPES as readonly string[]).includes(mime);
  const isDoc = (ALLOWED_DOC_EXTENSIONS as readonly string[]).includes(ext);
  if (!isImage && !isVideo && !isAudio && !isDoc) {
    return {
      valid: false,
      error: `Tipo de archivo no permitido (${ext || mime}). Solo se permiten imágenes, videos, audios y documentos.`,
    };
  }
  return { valid: true };
}
